/**
 * tdesign v1.6.6
 * (c) 2023 tdesign
 * @license MIT
 */

import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import isUndefined from 'lodash/isUndefined';
import isBoolean from 'lodash/isBoolean';
import omit from 'lodash/omit';
import get from 'lodash/get';
import log from '../log/log.js';

var nodeKey = "__tdesign_tree-node__";
var TreeNodeModel = /*#__PURE__*/function () {
  function TreeNodeModel(node) {
    _classCallCheck(this, TreeNodeModel);
    _defineProperty(this, nodeKey, void 0);
    this[nodeKey] = node;
  }
  _createClass(TreeNodeModel, [{
    key: "value",
    get: function get() {
      var node = this[nodeKey];
      return node.value;
    }
  }, {
    key: "label",
    get: function get() {
      var node = this[nodeKey];
      return node.label;
    }
  }, {
    key: "data",
    get: function get() {
      var node = this[nodeKey];
      return node.data;
    }
  }, {
    key: "actived",
    get: function get() {
      var node = this[nodeKey];
      return node.actived;
    }
  }, {
    key: "expanded",
    get: function get() {
      var node = this[nodeKey];
      return node.expanded;
    }
  }, {
    key: "checked",
    get: function get() {
      var node = this[nodeKey];
      return node.checked;
    }
  }, {
    key: "indeterminate",
    get: function get() {
      var node = this[nodeKey];
      return node.indeterminate;
    }
  }, {
    key: "loading",
    get: function get() {
      var node = this[nodeKey];
      return node.loading;
    }
  }, {
    key: "getLevel",
    value: function getLevel() {
      var node = this[nodeKey];
      return node.getLevel();
    }
  }, {
    key: "getIndex",
    value: function getIndex() {
      var node = this[nodeKey];
      return node.getIndex();
    }
  }, {
    key: "isFirst",
    value: function isFirst() {
      var node = this[nodeKey];
      return node.isFirst();
    }
  }, {
    key: "isLast",
    value: function isLast() {
      var node = this[nodeKey];
      return node.isLast();
    }
  }, {
    key: "isLeaf",
    value: function isLeaf() {
      var node = this[nodeKey];
      return node.isLeaf();
    }
  }, {
    key: "insertBefore",
    value: function insertBefore(newData) {
      var node = this[nodeKey];
      return node.insertBefore(newData);
    }
  }, {
    key: "insertAfter",
    value: function insertAfter(newData) {
      var node = this[nodeKey];
      return node.insertAfter(newData);
    }
  }, {
    key: "appendData",
    value: function appendData(data) {
      var node = this[nodeKey];
      return node.append(data);
    }
  }, {
    key: "getPath",
    value: function getPath() {
      var node = this[nodeKey];
      var nodes = node.getPath();
      return nodes.map(function (item) {
        return item.getModel();
      });
    }
  }, {
    key: "getParent",
    value: function getParent() {
      var _node$parent;
      var node = this[nodeKey];
      return (_node$parent = node.parent) === null || _node$parent === void 0 ? void 0 : _node$parent.getModel();
    }
  }, {
    key: "getParents",
    value: function getParents() {
      var node = this[nodeKey];
      var nodes = node.getParents();
      return nodes.map(function (item) {
        return item.getModel();
      });
    }
  }, {
    key: "getRoot",
    value: function getRoot() {
      var node = this[nodeKey];
      var root = node.getRoot();
      return root === null || root === void 0 ? void 0 : root.getModel();
    }
  }, {
    key: "getSiblings",
    value: function getSiblings() {
      var node = this[nodeKey];
      var nodes = node.getSiblings();
      return nodes.map(function (item) {
        return item.getModel();
      });
    }
  }, {
    key: "getChildren",
    value: function getChildren(deep) {
      var node = this[nodeKey];
      var childrenModel = false;
      var children = node.children;
      if (Array.isArray(children)) {
        if (children.length > 0) {
          if (deep) {
            var nodes = node.walk();
            nodes.shift();
            childrenModel = nodes.map(function (item) {
              return item.getModel();
            });
          } else {
            childrenModel = children.map(function (item) {
              return item.getModel();
            });
          }
        } else {
          childrenModel = false;
        }
      } else if (isBoolean(children)) {
        childrenModel = children;
      }
      return childrenModel;
    }
  }, {
    key: "remove",
    value: function remove(value) {
      var node = this[nodeKey];
      if (!value) {
        node.remove();
        return;
      }
      var tree = node.tree;
      var targetNode = tree.getNode(value);
      if (!targetNode) {
        log.warnOnce("Tree", "`".concat(value, "` is not exist"));
        return;
      }
      var parents = targetNode.getParents();
      var parentValues = parents.map(function (pnode) {
        return pnode.value;
      });
      if (parentValues.indexOf(node.value) < 0) {
        log.warnOnce("Tree", "`".concat(value, "` is not a childNode of current node"));
        return;
      }
      targetNode.remove();
    }
  }, {
    key: "setData",
    value: function setData(data) {
      var node = this[nodeKey];
      var _data = omit(data, ["children", "value", "label", "disabled"]);
      var keys = node.tree.config.keys;
      var dataValue = get(data, (keys === null || keys === void 0 ? void 0 : keys.value) || "value");
      var dataLabel = get(data, (keys === null || keys === void 0 ? void 0 : keys.label) || "label");
      var dataDisabled = get(data, (keys === null || keys === void 0 ? void 0 : keys.disabled) || "disabled");
      if (!isUndefined(dataValue)) _data.value = dataValue;
      if (!isUndefined(dataLabel)) _data.label = dataLabel;
      if (!isUndefined(dataDisabled)) _data.disable = dataDisabled;
      Object.assign(node.data, _data);
      Object.assign(node, _data);
    }
  }]);
  return TreeNodeModel;
}();
function createNodeModel(node) {
  var model = new TreeNodeModel(node);
  return model;
}

export { TreeNodeModel, createNodeModel, nodeKey };
//# sourceMappingURL=tree-node-model.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/tdesign-vue/es/_common/js/tree/tree-node-model.js
// module id = null
// module chunks = 